import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
    CheckIcon,
    // XCircle,
    ChevronDown,
    // XIcon,
} from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";
import { cn } from "@/lib/utils";
import { Separator } from "../ui/separator";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "../ui/command";

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const _multiSelectVariants = cva(
    "m-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300",
    {
        variants: {
            _variant: {
                default:
                    "border-foreground/10 text-foreground bg-card hover:bg-card/80",
                secondary:
                    "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
                destructive:
                    "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
                inverted: "inverted",
            },
        },
        defaultVariants: {
            _variant: "default",
        },
    }
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof _multiSelectVariants> {
    /**
     * An array of option objects to be displayed in the multi-select component.
     * Each option object has a label, value, and an optional icon.
     */
    options: {
        /** The text to display for the option. */
        label: string | React.ReactNode;
        /** The unique value associated with the option. */
        value: string;
        /** Optional icon component to display alongside the option. */
        icon?: React.ComponentType<{ className?: string }>;
        logo?: string;
        disabled?: boolean; // added
    }[];

    /**
     * Callback function triggered when the selected values change.
     * Receives an array of the new selected values.
     */
    onValueChange: (value: string[]) => void;

    /** The default selected values when the component mounts. */
    defaultValue: string[];

    /**
     * Placeholder text to be displayed when no values are selected.
     * Optional, defaults to "Select options".
     */
    placeholder?: string;

    /**
     * Animation duration in seconds for the visual effects (e.g., bouncing badges).
     * Optional, defaults to 0 (no animation).
     */
    animation?: number;

    /**
     * Maximum number of items to display. Extra selected items will be summarized.
     * Optional, defaults to 3.
     */
    maxCount?: number;

    /**
     * The modality of the popover. When set to true, interaction with outside elements
     * will be disabled and only popover content will be visible to screen readers.
     * Optional, defaults to false.
     */
    modalPopover?: boolean;

    /**
     * If true, renders the multi-select component as a child of another component.
     * Optional, defaults to false.
     */
    asChild?: boolean;

    /**
     * Additional class names to apply custom styles to the multi-select component.
     * Optional, can be used to add custom styles.
     */
    className?: string;
    /**
     * Maximum number of items that can be selected.
     * If not provided, no limit will be enforced.
     */
    maxSelectableLimit?: number;
    /**
     * Whether to show the "Select All" option at the top of the options list.
     * Optional, defaults to true.
     */
    showSelectAll?: boolean;
    /**
     * When true, renders PopoverContent inline (not in a Portal). Use inside modals/dialogs to keep scrolling working.
     */
    disablePortal?: boolean;
    value?: string[]; // added (controlled mode)
}

const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
    (
        {
            options,
            onValueChange,
            _variant,
            defaultValue = [],
            placeholder = "Select options",
            animation = 0,
            maxCount = 3,
            modalPopover = false,
            className,
            maxSelectableLimit,
            showSelectAll = true,
            disablePortal = false,
            value, 
            ...props
        },
        ref
    ) => {
        const [selectedValues, setSelectedValues] =
            React.useState<string[]>(value ?? defaultValue);

        // keep internal state in sync when parent controls value
        React.useEffect(() => {
            if (Array.isArray(value)) {
                setSelectedValues(value);
            }
        }, [value]);

        React.useEffect(() => {
            if (!value) {
                setSelectedValues(defaultValue);
            }
        }, [defaultValue, value]);

        const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
        const [isAnimating] = React.useState(false);

        const handleInputKeyDown = (
            event: React.KeyboardEvent<HTMLInputElement>
        ) => {
            if (event.key === "Enter") {
                setIsPopoverOpen(true);
            } else if (event.key === "Backspace" && !event.currentTarget.value) {
                const newSelectedValues = [...selectedValues];
                newSelectedValues.pop();
                setSelectedValues(newSelectedValues);
                onValueChange(newSelectedValues);
            }
        };

        const toggleOption = (val: string, disabled?: boolean) => {
            if (disabled) return; // block interaction if disabled
            if (
                maxSelectableLimit &&
                !selectedValues.includes(val) &&
                selectedValues.length >= maxSelectableLimit
            ) {
                return;
            }
            const newSelectedValues = selectedValues.includes(val)
                ? selectedValues.filter((v) => v !== val)
                : [...selectedValues, val];
            setSelectedValues(newSelectedValues);
            if (value) {
                onValueChange(newSelectedValues);
            }
        };

        const handleApply = () => {
            setIsPopoverOpen(false);
            onValueChange(selectedValues);
        };

        const handleClear = () => {
            setSelectedValues([]);
        };

        return (
            <TooltipProvider>
                <Popover
                    open={isPopoverOpen}
                    onOpenChange={setIsPopoverOpen}
                    modal={modalPopover}
                >
                    <PopoverTrigger asChild>
                        <Button
                            ref={ref}
                            {...props}
                            onClick={() => setIsPopoverOpen(true)}
                            variant={'white'}
                            className={cn(
                                "flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-white hover:bg-gray-50 text-gray-900 border-gray-300",
                                className
                            )}
                        >
                            {selectedValues.length > 0 ? (
                                <div className="flex justify-between items-center w-full overflow-hidden">
                                    <div className="flex flex-wrap items-center min-w-0 flex-1 pr-2">
                                        {selectedValues.slice(0, maxCount).map((value) => {
                                            const option = options.find((o) => o.value === value);
                                            const IconComponent = option?.icon;
                                            return (
                                                <Badge
                                                    key={value}
                                                    className={cn(
                                                        "max-w-full truncate m-1 bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-300",
                                                        isAnimating ? "animate-bounce" : ""
                                                    )}
                                                    style={{ animationDuration: `${animation}s` }}
                                                >
                                                    <div className="flex items-center overflow-hidden">
                                                        {IconComponent && (
                                                            <IconComponent className="h-4 w-4 mr-2 flex-shrink-0" />
                                                        )}
                                                        <span className="truncate max-w-full block text-gray-800">
                                                            {typeof option?.label === 'string' ? option.label : value}
                                                        </span>
                                                         {/* TODO: Introduce later */}
                                                        {/* <XCircle
                                                            className="ml-2 h-4 w-4 cursor-pointer flex-shrink-0 text-gray-600 hover:text-gray-800"
                                                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                toggleOption(value);
                                                            }}
                                                        /> */}
                                                    </div>
                                                </Badge>
                                            );
                                        })}
                                        
                                        {selectedValues.length > maxCount && (
                                            <Badge
                                                className={cn(
                                                    "bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200 m-1",
                                                    isAnimating ? "animate-bounce" : ""
                                                )}
                                                style={{ animationDuration: `${animation}s` }}
                                            >
                                                {`+ ${selectedValues.length - maxCount} more`}
                                                 {/* TODO: Introduce later */}
                                                {/* <XCircle
                                                    className="ml-2 h-4 w-4 cursor-pointer text-gray-600 hover:text-gray-800"
                                                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        setSelectedValues(selectedValues.slice(0, maxCount));
                                                        if (value) {
                                                            onValueChange(selectedValues.slice(0, maxCount));
                                                        }
                                                    }}
                                                /> */}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between flex-shrink-0">
                                        {/* TODO: Introduce later */}
                                        {/* <XIcon
                                            className="h-4 mx-2 cursor-pointer text-gray-500 hover:text-gray-700 flex-shrink-0 z-60"
                                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                handleClear();
                                            }}
                                        /> */}
                                        <Separator
                                            orientation="vertical"
                                            className="flex min-h-6 h-full bg-gray-300"
                                        />
                                        <ChevronDown className="h-4 mx-2 cursor-pointer text-gray-500 hover:text-gray-700 flex-shrink-0" />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between w-full mx-auto">
                                    <span className="text-sm text-gray-500 mx-3 truncate">
                                        {placeholder}
                                    </span>
                                    <ChevronDown className="h-4 cursor-pointer text-gray-500 hover:text-gray-700 mx-2" />
                                </div>
                            )}
                        </Button>
                    </PopoverTrigger>
                    {/* Conditional portal handling to avoid inert/aria-hidden issues inside Dialog */}
                    {disablePortal ? (
                        <PopoverPrimitive.Content
                            side="top"
                            align="start"
                            onEscapeKeyDown={() => setIsPopoverOpen(false)}
                            className={cn(
                                "z-50 min-w-[400px] p-0 max-h-[340px] overflow-auto rounded-md border bg-white shadow-md outline-none",
                            )}
                        >
                            <Command className="bg-white">
                                <CommandInput
                                    placeholder="Search Items..."
                                    className="bg-white text-gray-600 placeholder:text-gray-500 border-gray-200"
                                    onKeyDown={handleInputKeyDown}
                                />
                                <CommandList className="max-h-[300px] overflow-y-auto">
                                    <CommandEmpty className="text-gray-500 text-center py-6">No Items found.</CommandEmpty>
                                    <CommandGroup className="bg-white p-1">
                                        {showSelectAll && (
                                            <CommandItem
                                                key="all"
                                                onSelect={() => {
                                                    if (selectedValues.length === options.length) {
                                                        setSelectedValues([]);
                                                    } else {
                                                        const limited = maxSelectableLimit
                                                            ? options.slice(0, maxSelectableLimit).map((o) => o.value)
                                                            : options.map((o) => o.value);
                                                        setSelectedValues(limited);
                                                    }
                                                }}
                                                className="cursor-pointer hover:bg-gray-100 text-gray-900 px-3 py-2 mx-1 my-0.5 rounded-sm"
                                            >
                                                <div
                                                    className={cn(
                                                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-gray-400",
                                                        selectedValues.length === options.length
                                                            ? "bg-black text-white"
                                                            : "bg-white [&_svg]:invisible"
                                                    )}
                                                >
                                                    <CheckIcon className="h-4 w-4" />
                                                </div>
                                                <span className="text-gray-900">Select All</span>
                                            </CommandItem>)}

                                        {options.map((option, index) => {
                                            const isSelected = selectedValues.includes(option.value);
                                            const limitDisabled =
                                                maxSelectableLimit &&
                                                !isSelected &&
                                                selectedValues.length >= maxSelectableLimit;
                                            const isDisabled = option.disabled || limitDisabled;

                                            const optionContent = (
                                                <CommandItem
                                                    key={`${option.value}-${index}`}
                                                    onSelect={() => toggleOption(option.value, isDisabled as boolean)}
                                                    className={cn(
                                                        "cursor-pointer flex items-center justify-between",
                                                        isDisabled ? "opacity-60 cursor-not-allowed" : ""
                                                    )}
                                                >
                                                    <div className="flex items-center w-full">
                                                        <div
                                                            className={cn(
                                                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-gray-400 flex-shrink-0",
                                                                isSelected
                                                                    ? "bg-black text-white"
                                                                    : "bg-white [&_svg]:invisible"
                                                            )}
                                                        >
                                                            <CheckIcon className="h-4 w-4" />
                                                        </div>
                                                        {/* preserve complex custom label (could contain buttons) */}
                                                        <div className="flex-1 min-w-0 overflow-hidden">
                                                            {typeof option.label === 'string' ? (
                                                                <span className="text-gray-900 block">{option.label}</span>
                                                            ) : (
                                                                <div className="text-gray-900 w-full">{option.label}</div>
                                                            )}
                                                        </div>
                                                        {option.logo && (
                                                            <img
                                                                src={option.logo}
                                                                alt={`${option.label} logo`}
                                                                className="ml-2 h-4 w-4 flex-shrink-0"
                                                            />
                                                        )}
                                                    </div>
                                                </CommandItem>
                                            );

                                            return isDisabled && !option.disabled ? (
                                                // limit-based disable tooltip (unchanged)
                                                <Tooltip key={`${option.value}-${index}-tooltip`}>
                                                    <TooltipTrigger asChild>
                                                        <div>{optionContent}</div>
                                                    </TooltipTrigger>
                                                    <TooltipContent className="bg-gray-900 text-white">
                                                        <p>Select up to {maxSelectableLimit} items or adjust your selection</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            ) : (
                                                <div key={`${option.value}-${index}`}>{optionContent}</div>
                                            );
                                        })}

                                    </CommandGroup>
                                </CommandList>
                                <CommandSeparator className="bg-gray-200" />
                                <div className="flex items-center justify-between gap-2 px-2 py-2 bg-white border-t border-gray-200">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleClear()}
                                        className="flex-1 justify-center cursor-pointer text-gray-700 hover:bg-gray-100"
                                    >
                                        Clear
                                    </Button>
                                    <Separator
                                        orientation="vertical"
                                        className="flex min-h-6 h-full bg-gray-300"
                                    />
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleApply}
                                        className="flex-1 justify-center cursor-pointer text-orange-600 hover:bg-orange-50 font-medium"
                                    >
                                        Apply
                                    </Button>
                                </div>
                            </Command>
                        </PopoverPrimitive.Content>
                    ) : (
                        <PopoverContent
                            side={"top"}
                            className="w-auto min-w-[260px] p-0 max-h-[340px] overflow-auto"
                            align="start"
                            onEscapeKeyDown={() => setIsPopoverOpen(false)}
                        >
                            <Command className="bg-white">
                                <CommandInput
                                    placeholder="Search items..."
                                    className="bg-white text-gray-600 placeholder:text-gray-500 border-gray-200"
                                    onKeyDown={handleInputKeyDown}
                                />
                                <CommandList className="max-h-[300px] overflow-y-auto">
                                    <CommandEmpty className="text-gray-500 text-center py-6">No items found.</CommandEmpty>
                                    <CommandGroup className="bg-white p-1">
                                        {showSelectAll && (
                                            <CommandItem
                                                key="all"
                                                onSelect={() => {
                                                    if (selectedValues.length === options.length) {
                                                        setSelectedValues([]);
                                                    } else {
                                                        const limited = maxSelectableLimit
                                                            ? options.slice(0, maxSelectableLimit).map((o) => o.value)
                                                            : options.map((o) => o.value);
                                                        setSelectedValues(limited);
                                                    }
                                                }}
                                                className="cursor-pointer hover:bg-gray-100 text-gray-900 px-3 py-2 mx-1 my-0.5 rounded-sm"
                                            >
                                                <div
                                                    className={cn(
                                                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-gray-400",
                                                        selectedValues.length === options.length
                                                            ? "bg-black text-white"
                                                            : "bg-white [&_svg]:invisible"
                                                    )}
                                                >
                                                    <CheckIcon className="h-4 w-4" />
                                                </div>
                                                <span className="text-gray-900">Select All</span>
                                            </CommandItem>)}

                                        {options.map((option, index) => {
                                            const isSelected = selectedValues.includes(option.value);
                                            const limitDisabled =
                                                maxSelectableLimit &&
                                                !isSelected &&
                                                selectedValues.length >= maxSelectableLimit;
                                            const isDisabled = option.disabled || limitDisabled;

                                            const optionContent = (
                                                <CommandItem
                                                    key={`${option.value}-${index}`}
                                                    onSelect={() => toggleOption(option.value, isDisabled as boolean)}
                                                    className={cn(
                                                        "cursor-pointer flex items-center justify-between",
                                                        isDisabled ? "opacity-60 cursor-not-allowed" : ""
                                                    )}
                                                >
                                                    <div className="flex items-center w-full">
                                                        <div
                                                            className={cn(
                                                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-gray-400 flex-shrink-0",
                                                                isSelected
                                                                    ? "bg-black text-white"
                                                                    : "bg-white [&_svg]:invisible"
                                                            )}
                                                        >
                                                            <CheckIcon className="h-4 w-4" />
                                                        </div>
                                                        <div className="flex-1 min-w-0 overflow-hidden">
                                                            {typeof option.label === 'string' ? (
                                                                <span className="text-gray-900 block">{option.label}</span>
                                                            ) : (
                                                                <div className="text-gray-900 w-full">{option.label}</div>
                                                            )}
                                                        </div>
                                                        {option.logo && (
                                                            <img
                                                                src={option.logo}
                                                                alt={`${option.label} logo`}
                                                                className="ml-2 h-4 w-4 flex-shrink-0"
                                                            />
                                                        )}
                                                    </div>
                                                </CommandItem>
                                            );

                                            return isDisabled && !option.disabled ? (
                                                <Tooltip key={`${option.value}-${index}-tooltip`}>
                                                    <TooltipTrigger asChild>
                                                        <div>{optionContent}</div>
                                                    </TooltipTrigger>
                                                    <TooltipContent className="bg-gray-900 text-white">
                                                        <p>Select up to {maxSelectableLimit} items or adjust your selection</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            ) : (
                                                <div key={`${option.value}-${index}`}>{optionContent}</div>
                                            );
                                        })}

                                    </CommandGroup>
                                </CommandList>
                                <CommandSeparator className="bg-gray-200" />
                                <div className="flex items-center justify-between gap-2 px-2 py-2 bg-white border-t border-gray-200">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleClear()}
                                        className="flex-1 justify-center cursor-pointer text-gray-700 hover:bg-gray-100"
                                    >
                                        Clear
                                    </Button>
                                    <Separator
                                        orientation="vertical"
                                        className="flex min-h-6 h-full bg-gray-300"
                                    />
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleApply}
                                        className="flex-1 justify-center cursor-pointer text-orange-600 hover:bg-orange-50 font-medium"
                                    >
                                        Apply
                                    </Button>
                                </div>
                            </Command>
                        </PopoverContent>
                    )}
                </Popover>
            </TooltipProvider>
        );
    }
);

MultiSelect.displayName = "MultiSelect";

export { MultiSelect };
